import { Card, Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS, CHART_COLORS } from '../../../../constants/styles';
import { getDateRangeBounds } from '../../../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { DateRangeType } from '../../../../types/phlebotomist-dashboard';
import { DateRangeSelect } from '../../../filters/DateRangeSelect';
import { LocationSelect } from '../../../filters/LocationMultiSelect';
import {
  IOfficeOption,
  OfficeMultiSelect,
} from '../../../filters/OfficeMultiSelect';
import { RegionMultiSelect } from '../../../filters/RegionMultiSelect';
import { IRecruiterFilters } from '../../utils/recruiterFilters';

const { Text } = Typography;

interface IRoleRestriction {
  hideRollingDates: boolean;
  hideCalendarDates: boolean;
}

export interface IFilterBarProps {
  contentWidth: number;
  filters: IRecruiterFilters;
  regions: { id: string; name: string }[];
  offices: IOfficeOption[];
  regionsLoading: boolean;
  officesLoading: boolean;
  isRegionLocked: boolean;
  isOfficeLocked: boolean;
  roleRestriction: IRoleRestriction;
  onDateRangeChange: (
    dateRange: DateRangeType,
    customDateRange?: { start: Date; end: Date }
  ) => void;
  onRegionChange: (regionIds: string[]) => void;
  onOfficeChange: (officeIds: string[]) => void;
  onLocationChange: (locationIds: string[]) => void;
}

export const FilterBar: FC<IFilterBarProps> = ({
  contentWidth,
  filters,
  regions,
  offices,
  regionsLoading,
  officesLoading,
  isRegionLocked,
  isOfficeLocked,
  roleRestriction,
  onDateRangeChange,
  onRegionChange,
  onOfficeChange,
  onLocationChange,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard');
  const { start, end } = getDateRangeBounds(
    filters.dateRange,
    filters.customDateRange
  );
  const isMobile = contentWidth < 768;
  const rowStyle = {
    flex: 1,
    minWidth: isMobile ? '100%' : 'calc(50% - 8px)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  } as const;
  const labelStyle = { whiteSpace: 'nowrap', minWidth: 80 } as const;

  return (
    <Card
      size="small"
      style={{
        backgroundColor: CHART_COLORS.filterBar.current.background,
        borderColor: CHART_COLORS.filterBar.current.border,
      }}
    >
      <Text
        style={{
          display: 'block',
          textAlign: 'center',
          color: BRAND_COLORS.primary.deepNavy,
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        {' – '}
        {end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </Text>

      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 16 }}
      >
        <div style={rowStyle}>
          <Text type="secondary" style={labelStyle}>
            {t('filtersDateRange')}:
          </Text>
          <div style={{ flex: 1 }}>
            <DateRangeSelect
              value={filters.dateRange}
              customRange={filters.customDateRange}
              onChange={onDateRangeChange}
              order="calendar-first"
              hideRolling={roleRestriction.hideRollingDates}
              hideCalendar={roleRestriction.hideCalendarDates}
            />
          </div>
        </div>

        <div style={rowStyle}>
          <Text type="secondary" style={labelStyle}>
            {t('filtersRegion')}:
          </Text>
          <div style={{ flex: 1 }}>
            <RegionMultiSelect
              value={filters.regionIds}
              onChange={onRegionChange}
              regions={regions}
              loading={regionsLoading}
              disabled={isRegionLocked}
            />
          </div>
        </div>

        {/* Office filter hidden for now — kept in code for future use */}
        <div style={{ ...rowStyle, display: 'none' }}>
          <Text type="secondary" style={labelStyle}>
            {t('filtersOffice')}:
          </Text>
          <div style={{ flex: 1 }}>
            <OfficeMultiSelect
              value={filters.officeIds}
              onChange={onOfficeChange}
              offices={offices}
              regionIds={filters.regionIds}
              loading={officesLoading}
              disabled={isOfficeLocked}
            />
          </div>
        </div>

        <div style={rowStyle}>
          <Text type="secondary" style={labelStyle}>
            {t('filtersLocation')}:
          </Text>
          <div style={{ flex: 1 }}>
            <LocationSelect
              value={filters.locationIds}
              onChange={onLocationChange}
              regionIds={filters.regionIds}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};
